import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@material-ui/core/Button";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import PiiAlertDialog from "./PiiAlertDialog";
import styles from "./PiiOverrideDialog.module.scss";

const REASON_KEYS = [
  "reasonNotPersonalInformation",
  "reasonTestOrSampleData",
  "reasonTechnicalOrSystemData",
  "reasonAlreadyMasked",
  "reasonOther",
];

const OTHER_TEXT_MAX_LENGTH = 500;

/**
 * "Mark detection as safe" popup for a single PII hit. Reason is picked from
 * a fixed list; choosing "Other" reveals a free-text box whose text becomes
 * the override reason sent to the backend (in place of the "Other" label).
 */
const PiiOverrideDialog = ({ activeHit, onCancel, onOverride }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [showDescriptionError, setShowDescriptionError] = useState(false);

  useEffect(() => {
    setSelectedReason("");
    setOtherText("");
    setShowDescriptionError(false);
  }, [activeHit]);

  const isOtherSelected = selectedReason === "reasonOther";

  const handleReasonChange = e => {
    setSelectedReason(e.target.value);
    setShowDescriptionError(false);
  };

  const handleOtherTextChange = e => {
    setOtherText(e.target.value);
    if (e.target.value.trim().length > 0) setShowDescriptionError(false);
  };

  const handleSubmit = () => {
    // Submit is disabled until a reason is selected (see the `disabled` prop
    // below); the only inline-validated field once one is picked is the
    // "Other" reason's free-text description.
    if (isOtherSelected && otherText.trim().length === 0) {
      setShowDescriptionError(true);
      return;
    }
    if (!selectedReason) return;
    const finalReason = isOtherSelected ? otherText.trim() : t(`pii.${selectedReason}`);
    onOverride(activeHit, finalReason);
  };

  return (
    <PiiAlertDialog
      open={Boolean(activeHit)}
      onClose={onCancel}
      title={t("pii.markAsSafeTitle")}
      body={
        <div className={styles.body}>
          <Typography variant="body2" className={styles.subtitle}>
            {t("pii.markAsSafeSubtitle")}
          </Typography>
          <RadioGroup
            className={styles.radioGroup}
            value={selectedReason}
            onChange={handleReasonChange}
          >
            {REASON_KEYS.map(key => {
              const label = t(`pii.${key}`);
              return (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio classes={{ root: styles.radio, checked: styles.radioChecked }} />}
                  label={label}
                  classes={{ label: styles.radioLabel }}
                />
              );
            })}
          </RadioGroup>
          {isOtherSelected && (
            <div className={styles.otherTextFieldWrapper}>
              <TextField
                className={styles.otherTextField}
                multiline
                minRows={3}
                fullWidth
                variant="outlined"
                placeholder={t("pii.otherReasonPlaceholder")}
                value={otherText}
                onChange={handleOtherTextChange}
                inputProps={{ maxLength: OTHER_TEXT_MAX_LENGTH }}
                error={showDescriptionError}
                helperText={showDescriptionError ? t("pii.addDescriptionError") : undefined}
              />
            </div>
          )}
        </div>
      }
      footer={
        <>
          <Button className={styles.cancelButton} onClick={onCancel}>
            {t("pii.cancel")}
          </Button>
          <Button
            variant="contained"
            className={styles.submitButton}
            classes={{ disabled: styles.submitButtonDisabled }}
            onClick={handleSubmit}
            disabled={!selectedReason}
          >
            {t("pii.submit")}
          </Button>
        </>
      }
    />
  );
};

export default PiiOverrideDialog;
